// frontend/src/screens/VerifyEmail.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { REST_API_ORIGIN } from '../platform/endpoints';
import { localizedServerContent, localizedValue } from '../localization/presentationLanguage';

const copy = (es, en) => localizedValue({ es, en });

const API_BASE = REST_API_ORIGIN;

const SPIN_CSS = `
  @keyframes zodiacus-spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  @keyframes verify-dots {
    0%, 20% { opacity: 0.2; }
    50% { opacity: 1; }
    100% { opacity: 0.2; }
  }
  @media (prefers-reduced-motion: reduce) {
    .pause-spin { animation: none !important; }
    .verify-loading-dots { animation: none !important; }
  }
`;

export default function VerifyEmail() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token');
  const [status, setStatus] = useState('loading'); // loading | ok | error
  const [message, setMessage] = useState(() => copy('Verificando tu email…', 'Verifying your email...'));

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage(copy('Token de verificación ausente.', 'The verification token is missing.'));
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/auth/verify-email?token=${encodeURIComponent(token)}`, {
          method: 'GET',
          credentials: 'include',
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok) {
          setStatus('error');
          setMessage(localizedServerContent(data.message, copy('No se pudo verificar el email.', 'The email could not be verified.')));
          return;
        }
        setStatus('ok');
        setMessage(copy('¡Listo! Tu email fue verificado correctamente.', 'Done! Your email was verified successfully.'));
      } catch {
        if (!cancelled) {
          setStatus('error');
          setMessage(copy('Error de conexión con el servidor.', 'Could not connect to the server.'));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <div style={styles.container}>
      <style>{SPIN_CSS}</style>
      <div aria-hidden="true" style={styles.wheel} className="pause-spin" />
      <div style={styles.card}>
        <h1 style={styles.title}>{copy('Verificación de Email', 'Email verification')}</h1>

        {status === 'loading' && (
          <div style={styles.loadingWrap}>
            <div style={styles.spinner} aria-hidden="true" />
            <p style={styles.text}>
              {message}{' '}
              <span className="verify-loading-dots" style={styles.loadingDots}>•••</span>
            </p>
          </div>
        )}
        {status === 'ok' && (
          <>
            <p style={styles.success}>{message}</p>
            <button style={styles.button} onClick={() => navigate('/login')}>{copy('Ir a Iniciar Sesión', 'Go to sign in')}</button>
          </>
        )}
        {status === 'error' && (
          <>
            <p style={styles.error}>{message}</p>
            <p style={styles.text}>
              {copy('Si el enlace expiró, volvé a registrarte o pedí un nuevo correo desde la app.', 'If the link expired, register again or request a new email from the app.')}
            </p>
            <Link to="/welcome" style={styles.link}>{copy('Volver al inicio', 'Back to Home')}</Link>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    position: 'relative',
    overflow: 'hidden',
    background: 'radial-gradient(1200px 800px at 50% -10%, rgba(218,165,32,0.15), transparent 60%), linear-gradient(135deg, #1F0A3A 0%, #4B0082 55%, #2B0F5A 100%)',
    display: 'grid',
    placeItems: 'center',
    padding: 20,
  },
  wheel: {
    position: 'absolute',
    inset: 0,              // ocupa todo el contenedor
    margin: 'auto',        // centra el elemento (horizontal y vertical)
    width: 'min(90vmin, 740px)',
    height: 'min(90vmin, 740px)',
    backgroundImage: 'url("/logo/wheel.png"), url("/logo/Zodiacus.jpeg")',
    backgroundSize: 'contain',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    mixBlendMode: 'screen',
    WebkitMaskImage: 'radial-gradient(circle, rgba(0,0,0,1) 72%, rgba(0,0,0,0) 100%)',
    maskImage: 'radial-gradient(circle, rgba(0,0,0,1) 72%, rgba(0,0,0,0) 100%)',
    filter: 'brightness(1.06) contrast(1.08) drop-shadow(0 10px 28px rgba(0,0,0,0.4))',
    opacity: 0.22,
    animation: 'zodiacus-spin 60s linear infinite',
    zIndex: 0,
    pointerEvents: 'none',
    userSelect: 'none',
  },
  card: {
    maxWidth: 520,
    width: '100%',
    background: 'rgba(255,255,255,0.68)',
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
    border: '1px solid rgba(75,0,130,0.18)',
    borderRadius: 12,
    padding: '28px 24px',
    boxShadow: '0 8px 18px rgba(0,0,0,0.2)',
    color: '#2b2b2b',
    textAlign: 'center',
    zIndex: 2,
  },
  title: { marginTop: 0, color: '#4B0082' },
  loadingWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 10,
  },
  spinner: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    border: '3px solid rgba(75,0,130,0.18)',
    borderTopColor: '#DAA520',
    animation: 'zodiacus-spin 1s linear infinite',
  },
  loadingDots: {
    display: 'inline-block',
    animation: 'verify-dots 1.2s ease-in-out infinite',
    letterSpacing: 1,
  },
  text: { lineHeight: 1.6, margin: '8px 0' },
  success: { color: '#0a8a0a', fontWeight: 600 },
  error: { color: '#c62828', fontWeight: 600 },
  button: {
    marginTop: 14,
    backgroundColor: '#DAA520',
    color: '#4B0082',
    border: 'none',
    borderRadius: 8,
    padding: '10px 16px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  link: { color: '#4B0082', fontWeight: 700, textDecoration: 'underline', display: 'inline-block', marginTop: 8 },
};
